import React, {
  ForwardedRef,
  MouseEventHandler,
  forwardRef,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import styles from './Button.module.css';

import { useAppSelector } from '../../hooks/useRedux';
import { useSlice } from '../../hooks/useSlice';
import { Options } from '../../types/library';
import { TooltipRenderProps } from '../Tooltip/TooltipWrapper';

interface ButtonProps {
  type?: 'submit' | 'reset' | 'button';
  name?: string;
  classnames?: (string | { [key: string]: boolean })[];
  handleClick?: MouseEventHandler<HTMLButtonElement>;
  isBlindName?: boolean;
  children?: React.ReactNode;
  isChildrenTop?: boolean;
  handleMouseEnter?: () => void;
  handleMouseLeave?: () => void;
  disabled?: boolean;
  tooltipProps?: TooltipRenderProps;
}

const Button = forwardRef(
  (
    {
      type = 'button',
      name,
      classnames,
      handleClick,
      isBlindName,
      children,
      isChildrenTop = false,
      handleMouseEnter,
      handleMouseLeave,
      disabled,
      tooltipProps,
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const cx = classNames.bind(styles);
    const { selectOptions } = useSlice();

    const { layoutType }: Options = useAppSelector(selectOptions);

    const {
      ref: tooltipRef,
      onClick: tooltipOnClick,
      onMouseEnter: tooltipOnMouseEnter,
      onMouseLeave: tooltipOnMouseLeave,
      ...otherTooltipProps
    } = tooltipProps || {};

    const mergedHandleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (handleClick) {
        handleClick(e);
      }
      if (tooltipOnClick && typeof tooltipOnClick === 'function') {
        tooltipOnClick(e);
      }
    };

    const mergedHandleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (handleMouseEnter) {
        handleMouseEnter();
      }
      if (tooltipOnMouseEnter && typeof tooltipOnMouseEnter === 'function') {
        tooltipOnMouseEnter(e);
      }
    };

    const mergedHandleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (handleMouseLeave) {
        handleMouseLeave();
      }
      if (tooltipOnMouseLeave && typeof tooltipOnMouseLeave === 'function') {
        tooltipOnMouseLeave(e);
      }
    };

    const setMultipleRefs = (node: HTMLButtonElement | null) => {
      if (ref) {
        if ('current' in ref) {
          ref.current = node;
        } else {
          ref(node);
        }
      }

      if (tooltipRef && typeof tooltipRef === 'function') {
        tooltipRef(node);
      }
    };

    return (
      <button
        type={type}
        className={cx('btn', classnames, layoutType)}
        onClick={mergedHandleClick}
        onMouseEnter={mergedHandleMouseEnter}
        onMouseLeave={mergedHandleMouseLeave}
        ref={setMultipleRefs}
        disabled={disabled}
        {...otherTooltipProps}
      >
        {isChildrenTop && children}
        <span className={cx({ blind: isBlindName })}>{name}</span>
        {!isChildrenTop && children}
      </button>
    );
  }
);

export default Button;
